import { faExternalLink } from '@fortawesome/pro-regular-svg-icons';
import { observer } from 'mobx-react-lite';
import type { JSX } from 'react';
import React, { useEffect, useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';

import type { Form } from '@feathr/blackbox';
import { AlertV2 as Alert, Button, EAlertV2Type, Icon, Modal, toast } from '@feathr/components';
import type { IRedirectDomainOption } from '@feathr/extender/components/RedirectDomainSelect';
import RedirectDomainSelect from '@feathr/extender/components/RedirectDomainSelect';
import { useLocalUrl, useStore } from '@feathr/extender/state';
import { errorMessage } from '@feathr/hooks';

import FormCode from './FormCode';

import * as styles from './FormShareModal.css';

interface IProps {
  form: Form;
  isOpen: boolean;
  toggle: () => void;
}

function FormShareModal({ form, isOpen, toggle }: IProps): JSX.Element {
  const { RedirectDomains } = useStore();
  const { t } = useTranslation();
  const [activeTab] = useState<'embed' | 'feathr'>('embed');
  const [redirectDomainId, setRedirectDomainId] = useState<string | undefined>(undefined);
  const [contentDomain, setContentDomain] = useState<string | undefined>(undefined);
  const [shortCode, setShortCode] = useState<string | undefined>(undefined);
  const localUrl = useLocalUrl();

  const redirectDomains = RedirectDomains.list({
    filters: { _is_verified: true, id__ne: '000000000000000000000000' },
  });

  function handleSelectDomain({ name: label, id: value }: IRedirectDomainOption): void {
    setRedirectDomainId(value);
    setContentDomain(label);
  }

  async function handleGenerateShortCode(): Promise<void> {
    try {
      if (!redirectDomainId) {
        throw new Error('No domain selected');
      }

      const publishedForm = await form.ensurePublishedForm(redirectDomainId);
      if (!publishedForm.short_code) {
        throw new Error('The form does not have a short code.');
      }
      setShortCode(publishedForm.short_code);

      // This should probably never happen?
      if (!publishedForm) {
        const { short_code: newShortCode } = await form.createPublishedForm(redirectDomainId);
        setShortCode(newShortCode);
        return;
      }

      setShortCode(publishedForm.short_code);

      toast(t('Form published successfully to {{- domain}}', { domain: contentDomain }), {
        type: 'success',
      });
    } catch (error) {
      toast(t('Failed to generate short code:\n{{- error}}', { error: errorMessage(error, t) }), {
        type: 'error',
      });
    }
  }

  useEffect(() => {
    async function getShortCode(): Promise<void> {
      if (redirectDomainId) {
        try {
          const publishedForm = await form.getPublishedForm(redirectDomainId);
          setShortCode(publishedForm?.short_code);
        } catch (error) {
          toast(t('Failed to get short code:\n{{- error}}', { error: errorMessage(error, t) }), {
            type: 'error',
          });
        }
      }
    }

    getShortCode();
  }, [redirectDomainId]);

  const showLink = redirectDomains.models.length > 1;
  return (
    <Modal
      allowOverflow={true}
      bodyClassName={styles.root}
      description={t(
        "Forms can only be embedded on pages from domains that you've authorized Feathr to serve content from.",
      )}
      onClose={toggle}
      opened={isOpen}
      rightActions={
        <Button onClick={toggle} type={'secondary'}>
          {t('Cancel')}
        </Button>
      }
      // Made the modal larger so that the embed code is not cut off
      size={'lg'}
      title={t('Share your form')}
    >
      {/* TODO: Reimplement tabs when Feathr embed is ready in #4565. */}
      {/* <Tabs>
        <Tab active={activeTab === 'embed'} onClick={handleClickEmbedTab} title={t('Embed code')} />
        <Tab active={activeTab === 'feathr'} onClick={handleClickFeathrTab} title={t('Feathr')} />
      </Tabs> */}
      {activeTab === 'embed' && (
        <div className={styles.embed}>
          <Trans t={t}>
            Forms can only be embedded on pages from domains that you've authorized Feathr to serve
            content from.
          </Trans>
          {showLink && (
            <Button
              className={styles.link}
              href={localUrl('/settings/account/domains')}
              suffix={<Icon icon={faExternalLink} />}
              target={'_blank'}
              type={'link'}
            >
              {t('View authorized domains')}
            </Button>
          )}
          <RedirectDomainSelect
            domains={redirectDomains}
            onChange={handleSelectDomain}
            value={redirectDomainId}
          />
          {redirectDomainId && (
            <FormCode
              contentDomain={contentDomain}
              formName={form.name}
              handleGenerateCode={handleGenerateShortCode}
              shortCode={shortCode}
            />
          )}
          <Alert
            description={t(
              'Forms work on landing pages and external websites but will not function in email templates, even if code is copied.',
            )}
            title={t("Forms won't work in email templates.")}
            type={EAlertV2Type.warning}
          />
        </div>
      )}
      {/* TODO: Implement Feathr tab in #4565. */}
      {activeTab === 'feathr' && <div>Foo</div>}
    </Modal>
  );
}

export default observer(FormShareModal);
